/**
 * Reading the address an asset came from: which platform it is, and whether it
 * is one a YouTube import may be pointed at.
 *
 * Both answers are the same reading of a hostname, so they are in one file. The
 * three that used to answer them separately disagreed: the API imports a
 * `youtube-nocookie.com` address as a video and studio's import form sent it as
 * a link, and `gaming.youtube.com` imported correctly and was then drawn as a
 * stranger (ADR 000124).
 */

import { YouTubeDomainSchema } from "@scenix/core";

/**
 * The hostname of an address, lowercased and without a leading `www.`, or null
 * when there is no address to read.
 *
 * Never throws: this runs while rendering a list and on every keystroke in the
 * import form, and one bad value must take neither with it.
 */
function hostOf(address: string | null | undefined): string | null {
  if (!address) return null;
  try {
    const host = new URL(address.trim()).hostname.toLowerCase().replace(/^www\./, "");
    return host || null;
  } catch {
    return null;
  }
}

/**
 * Whether a host is one of `domains` or sits beneath one on a dot boundary.
 *
 * The dot is the whole of it. Without it `notyoutube.com` is a suffix of
 * `youtube.com` too, and that is the version of this check that gets written
 * once and read as correct forever (ADR 000122).
 */
function hostBeneath(host: string, domains: readonly string[]): boolean {
  return domains.some((domain) => host === domain || host.endsWith(`.${domain}`));
}

/**
 * Whether an address is one a YouTube import may be pointed at (ADR 000124).
 *
 * The mirror of the host half of `domain.ValidYouTubeSourceURL`, the way
 * `uploadAbandoned` is the mirror of `Asset.UploadAbandoned`. The domains are
 * the shared list, and matching on the parsed hostname is what makes this the
 * same answer the API gives: the regex this replaced matched `youtube.com/`
 * anywhere in the string, so an unrelated address carrying one in a query
 * parameter was offered YouTube's import modes and then refused by the API.
 *
 * The host is all it answers. `ValidYouTubeSourceURL` also refuses an address
 * carrying credentials, and a surface that called one of those something other
 * than YouTube would quietly create a link asset out of it instead of meeting
 * the refusal that names the field.
 */
export function isYouTubeAddress(address: string | null | undefined): boolean {
  const host = hostOf(address);
  return host !== null && hostBeneath(host, YouTubeDomainSchema.options);
}

/**
 * Where a piece of content came from, as somebody scanning a list needs it.
 *
 * A source URL is only useful if it is one click away and recognisable without
 * reading it (ADR 000060). Recognised platforms are named and drawn; anything
 * else is still a link, labelled with its host, because "somewhere else" is
 * more useful than nothing and a wrong logo is worse than a plain one.
 */
export interface SourcePlatform {
  /** What to call it, in a tooltip and to a screen reader. */
  name: string;
  /** Which glyph to draw. `link` is the honest fallback. */
  glyph: "youtube" | "vimeo" | "link";
  /** Tints the icon. Absent means the interface's own ink. */
  brand?: string;
}

/**
 * Matched on the registrable domain rather than on the exact hostname, so a
 * watch page, a short link, an embed and any subdomain all read the same.
 *
 * YouTube's domains are the shared list, because the API decides what a YouTube
 * import is from that same list and a row drawn as a stranger is a row whose
 * import nobody trusts. Vimeo's is a studio list and stays one: nothing but
 * this glyph rests on it, so there is nothing for it to drift from.
 *
 * Deliberately short. A platform earns a line here when somebody's corpus
 * actually comes from it, and a mark nobody can draw accurately is better left
 * as the generic one than approximated.
 */
const PLATFORMS: { domains: readonly string[]; platform: SourcePlatform }[] = [
  {
    domains: YouTubeDomainSchema.options,
    platform: { name: "YouTube", glyph: "youtube", brand: "#ff0033" },
  },
  {
    domains: ["vimeo.com"],
    platform: { name: "Vimeo", glyph: "vimeo", brand: "#19b7ea" },
  },
];

/**
 * Reads a source URL as a platform, or null when there is nothing to read.
 */
export function platformOf(sourceUrl: string | null | undefined): SourcePlatform | null {
  const host = hostOf(sourceUrl);
  if (host === null) return null;

  for (const { domains, platform } of PLATFORMS) {
    if (hostBeneath(host, domains)) return platform;
  }
  // Named by its host, so an unrecognised source still says where it goes.
  return { name: host, glyph: "link" };
}

/**
 * The longest name the import form will derive from an address, in the bytes
 * the API counts.
 *
 * Shorter than the 300 the shared schema allows a name, and deliberately not
 * that number restated: this is the length of a line somebody scans down a
 * corpus listing, and a name the API would accept but nobody can read is not
 * the thing to aim at. What holds the two together is the test that parses a
 * derived name with `NewAssetSchema`, so a shared cap lowered beneath this one
 * fails rather than turning the form's imports into 400s.
 *
 * Characters, because that is what the cap on the other side is measured in
 * (ADR 000126). It used to be bytes, which was what Go's `len` counted before
 * that record and is no longer what anything counts. A parsed address is ASCII
 * either way, since the host comes back punycoded and the path and query come
 * back percent-encoded, so the change is only visible on the one input the two
 * never agreed on: the address this browser could not parse at all, which is
 * the fallback below.
 */
const DERIVED_NAME_LIMIT = 120;

/**
 * What to call an asset imported from an address, when nobody typed a name.
 *
 * The form asks for one field and the API requires two, so the name is the
 * form's to make up. It used to send the address whole, which meant an address
 * over 300 characters was refused as "name is too long": a sentence about a
 * field with no box on screen, on an import the person could do nothing about
 * but edit query parameters by hand (#488).
 *
 * The scheme goes, and a leading `www.`, because neither tells a reader
 * anything they cannot assume, and so does the fragment, which addresses a
 * place inside the page rather than the page. The host, the path and the query
 * stay in the order they were written and are cut from the end when they run
 * long, rather than the query being dropped wholesale: the most common import
 * there is is a YouTube watch URL, whose identity is `v=` in the query and
 * whose path is `/watch` for every video ever made. Cutting from the end keeps
 * the host, the path and the first parameters, which is where a person reads
 * what a link is.
 *
 * A name is a person's to correct afterwards (ADR 000060), so this has to be
 * recognisable rather than right.
 */
export function nameForAddress(address: string): string {
  const trimmed = address.trim();

  let readable = trimmed;
  try {
    const url = new URL(trimmed);
    const host = url.hostname.toLowerCase().replace(/^www\./, "");
    // A bare host keeps no trailing slash: "example.com" reads as a name and
    // "example.com/" reads as a mistake.
    const path = url.pathname === "/" ? "" : url.pathname;
    readable = `${host}${path}${url.search}`;
  } catch {
    // Not an address this browser can parse, which the field's own type="url"
    // already refuses. Naming it after what was typed is still better than
    // refusing it here, since the API is about to say what is wrong with it.
  }

  return shortenTo(readable, DERIVED_NAME_LIMIT);
}

/**
 * `text`, cut from the end until it fits in `limit` characters, with an
 * ellipsis standing where the rest was.
 *
 * Code point by code point, so the cut falls between characters. Slicing a
 * UTF-16 string at a fixed index can leave half a surrogate pair behind, which
 * is not a character at all and reaches the corpus listing as a replacement
 * mark. Iterating a string yields whole code points, which is why the budget is
 * spent one at a time rather than by index.
 */
function shortenTo(text: string, limit: number): string {
  const points = [...text];
  if (points.length <= limit) return text;

  // The ellipsis is one character of the budget, and it is spent before the cut
  // rather than after, so the result is the limit rather than one past it.
  return `${points.slice(0, limit - 1).join("")}…`;
}
